import { FC, useMemo } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeader,
} from "@ryde/controls";
import {
  IDialogContainerProps,
  PLURAL_KEY,
  RydeUiUnit,
  SINGLE_KEY,
  tableRowsAtomFamily,
} from "@ryde/shared";

import { BulkArchiveRelationErrorDisplayStyled } from "./bulk-archive-relation-error-display.styled";
import { RelationRowsIds } from "./relations-row-ids";

interface IMergedRow {
  name: string;
  id: string;
  relation: string;
  ids: string[];
}

interface IBulkArchiveRelationErrorDisplayDialog extends IDialogContainerProps {
  rejectFn: () => void;
  existingRelation: {
    [id: string]: {
      [readableName: string]: { id: string }[];
    };
  };
  entity: RydeUiUnit;
}

export const BulkArchiveRelationErrorDisplayDialog: FC<
  IBulkArchiveRelationErrorDisplayDialog
> = ({ onClose, existingRelation, entity }) => {
  const { t } = useTranslation();
  const rows = useRecoilValue(tableRowsAtomFamily(entity));

  const mergedData = useMemo((): IMergedRow[] => {
    const currentRows = rows ?? [];
    const rowsDictionary = currentRows?.reduce(
      (acc: { [id: number]: string }, row) => {
        acc[row.id] = row?.name ?? "";
        return acc;
      },
      {},
    );

    const tempData: IMergedRow[] = [];

    Object.entries(existingRelation).forEach(([id, relations]) => {
      Object.entries(relations).forEach(([relation, items]) => {
        const ids = items.map(item => item.id);
        tempData.push({ id, relation, ids, name: rowsDictionary?.[+id] });
      });
    });

    return tempData;
  }, [existingRelation, rows]);

  const mergedDataForCsv = useMemo(() => {
    return mergedData.map(row => ({
      [t(`${entity}.${SINGLE_KEY}`)]: row.name || row.id,
      [t("general.RELATION_ENTITY")]: t(`${row.relation}.${PLURAL_KEY}`),
      [t("general.RELATION_IDS")]: row.ids.join(", "),
    }));
  }, [entity, mergedData, t]);

  return (
    <ModalContainerStyled>
      <ModalHeader onClose={onClose}>{t("general.PLEASE_NOTE")}</ModalHeader>
      <ModalContentStyled $showContent>
        <BulkArchiveRelationErrorDisplayStyled>
          <h4 className="bulk-archive-relation-dialog-title">
            {t("errors.bulkArchiveRelationError")}
          </h4>

          <table className="excel-table">
            <thead>
              <tr>
                <th>{t(`${entity}.${SINGLE_KEY}`)}</th>
                <th>{t("general.RELATION_ENTITY")}</th>
                <th>{t("general.RELATION_IDS")}</th>
              </tr>
            </thead>
            <tbody>
              {mergedData.map((row, index) => {
                const isFirstRowForId =
                  index === 0 ||
                  mergedData[index].id !== mergedData[index - 1].id;

                return (
                  <tr key={`${row.id}-${row.relation}`}>
                    {isFirstRowForId ? (
                      <td
                        rowSpan={
                          mergedData.filter(item => item.id === row.id).length
                        }
                        className="id-cell"
                      >
                        {row.name || row.id}
                      </td>
                    ) : null}
                    <td>{t(`${row.relation}.${PLURAL_KEY}`)}</td>
                    <RelationRowsIds ids={row.ids} relation={row.relation} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </BulkArchiveRelationErrorDisplayStyled>
        <ModalFooterStyled>
          <CSVLink
            data={mergedDataForCsv}
            filename="bulk_archive_existing_relation.csv"
            className="download-btn"
            target="_blank"
          >
            {t(`general.EXPORT_CSV`)}
          </CSVLink>
        </ModalFooterStyled>
      </ModalContentStyled>
    </ModalContainerStyled>
  );
};
